import React, { useState, useEffect } from "react";
import { Edit2, History, Plus, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import {
    getSubscribedMembers,
    createSubscribedMember,
    updateSubscribedMember,
    deleteSubscribedMember,
} from "../../services/api";
import { parseTime, buildTime } from "../../utils/timeUtils";
import MealHistory from "../Subscription/MealHistory";

const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
const MEAL_PREFERENCES = ["Veg", "Non veg", "Fish", "Eggs"];
const BASE_OPTIONS = ["Rice", "Salad", "Keto", "Only Protein", "Pasta", "Sandwich"];
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = ["00", "15", "30", "45"];
const MERIDIEM = ["AM", "PM"];

const PLAN_OPTIONS = ["Mini salad bowl", "salad bowl", "mini protein bowl", "protein bowl", "Only Protein"];

const SubscribedMembersManagement = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [historyMember, setHistoryMember] = useState(null);

    const emptyMealPlan = {
        period: "Lunch",
        bowlType: "",
        proteinQuantity: "",
        deliveryTime: "",
        days: {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
            Sunday: [],
        }
    };

    const emptyForm = {
        name: "",
        email: "",
        mobile: "",
        numberOfMeals: "",
        customizationRequest: "",
        specialRequest: "",
        mealPlans: [{ ...emptyMealPlan }],
    };

    const [formData, setFormData] = useState(emptyForm);

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const res = await getSubscribedMembers();
            if (res.data && res.data.success) {
                setMembers(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching Subscribed Members:", err);
            toast.error("Failed to fetch Subscribed Members");
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingData(null);
        setFormData({ ...emptyForm, mealPlans: [{ ...emptyMealPlan }] });
        setShowForm(true);
    };

    const handleEdit = (member) => {
        setEditingData(member);
        const plans = member.mealPlans && member.mealPlans.length > 0
            ? member.mealPlans.map(plan => ({
                period: plan.period || "Lunch",
                bowlType: plan.bowlType || "",
                proteinQuantity: plan.proteinQuantity || "",
                deliveryTime: plan.deliveryTime || "",
                days: plan.days ? { ...plan.days } : { ...emptyMealPlan.days }
            }))
            : [{ ...emptyMealPlan }];

        setFormData({
            name: member.name,
            email: member.email,
            mobile: member.mobile || "",
            numberOfMeals: member.numberOfMeals || "",
            customizationRequest: member.customizationRequest || "",
            specialRequest: member.specialRequest || "",
            mealPlans: plans,
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this member?")) return;
        try {
            const res = await deleteSubscribedMember(id);
            if (res.data && res.data.success) {
                toast.success("Member deleted");
                fetchMembers();
            }
        } catch (err) {
            console.error("Error deleting member:", err);
            toast.error("Failed to delete member");
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleMealPlanChange = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.mealPlans];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, mealPlans: updated };
        });
    };

    // Single select within a group (meal preference / base) — clicking the active one clears it
    const handleDayPrefSelect = (index, day, type, group) => {
        setFormData(prev => {
            const updated = [...prev.mealPlans];
            const currentDays = updated[index].days[day] || [];
            const others = currentDays.filter(t => !group.includes(t));
            updated[index] = {
                ...updated[index],
                days: {
                    ...updated[index].days,
                    [day]: currentDays.includes(type) ? others : [...others, type]
                }
            };
            return { ...prev, mealPlans: updated };
        });
    };

    const addMealPlan = () => {
        setFormData(prev => ({ ...prev, mealPlans: [...prev.mealPlans, { ...emptyMealPlan }] }));
    };

    const removeMealPlan = (index) => {
        setFormData(prev => ({
            ...prev,
            mealPlans: prev.mealPlans.filter((_, i) => i !== index)
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingData && editingData._id) {
                const res = await updateSubscribedMember(editingData._id, formData);
                if (res.data && res.data.success) {
                    toast.success("Member updated");
                    fetchMembers();
                    setShowForm(false);
                }
            } else {
                const res = await createSubscribedMember(formData);
                if (res.data && res.data.success) {
                    toast.success("Member created");
                    fetchMembers();
                    setShowForm(false);
                }
            }
        } catch (err) {
            console.error("Error saving member:", err);
            toast.error("Failed to save member");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold text-gray-800">
                    Subscribed Members
                </h2>
                {!showForm && (
                    <button
                        onClick={handleAddNew}
                        className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Add Member</span>
                    </button>
                )}
            </div>

            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-800">
                            {editingData ? "Edit Member" : "New Member"}
                        </h3>
                        <button
                            onClick={() => setShowForm(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                <input type="text" name="name" value={formData.name} onChange={handleFormChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                                <input type="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
                                <input type="text" name="mobile" value={formData.mobile} onChange={handleFormChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Number of meals *</label>
                                <input type="text" name="numberOfMeals" value={formData.numberOfMeals} onChange={handleFormChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Customization Request</label>
                                <textarea name="customizationRequest" value={formData.customizationRequest} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" rows="3" placeholder="Any specific adjustments..."></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Special Request</label>
                                <textarea name="specialRequest" value={formData.specialRequest} onChange={handleFormChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" rows="3" placeholder="Allergies, delivery instructions..."></textarea>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">Meal Plans</h3>
                                <button type="button" onClick={addMealPlan} className="flex items-center space-x-1 bg-white border border-gray-300 px-3 py-1.5 rounded text-sm text-green-700 hover:bg-green-50 font-medium transition cursor-pointer">
                                    <Plus className="w-4 h-4" /> <span>Add new meal period</span>
                                </button>
                            </div>

                            {formData.mealPlans.map((plan, index) => (
                                <div key={index} className="border border-green-200 rounded-xl p-6 relative bg-green-50/30 shadow-sm">
                                    {formData.mealPlans.length > 1 && (
                                        <button type="button" onClick={() => removeMealPlan(index)} className="absolute top-4 right-4 text-red-500 hover:bg-red-100 p-2 rounded-full transition cursor-pointer" title="Delete Period">
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Meal Period</label>
                                            <select value={plan.period} onChange={(e) => handleMealPlanChange(index, "period", e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-green-500 bg-white shadow-sm font-medium">
                                                <option value="Lunch">Lunch</option>
                                                <option value="Dinner">Dinner</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Time</label>
                                            <div className="flex gap-2">
                                                {[["h", HOURS, "HH"], ["m", MINUTES, "MM"], ["ap", MERIDIEM, "AM/PM"]].map(([part, options, placeholder]) => (
                                                    <select
                                                        key={part}
                                                        value={parseTime(plan.deliveryTime)[part]}
                                                        onChange={(e) => handleMealPlanChange(index, "deliveryTime", buildTime({ ...parseTime(plan.deliveryTime), [part]: e.target.value }))}
                                                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-green-500 bg-white shadow-sm"
                                                    >
                                                        <option value="">{placeholder}</option>
                                                        {options.map(o => (
                                                            <option key={o} value={o}>{o}</option>
                                                        ))}
                                                    </select>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Plan</label>
                                            <select value={plan.bowlType} onChange={(e) => handleMealPlanChange(index, "bowlType", e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-green-500 bg-white shadow-sm">
                                                <option value="">Select Plan</option>
                                                {PLAN_OPTIONS.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {plan.bowlType === "Only Protein" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Protein Quantity</label>
                                                <input type="text" value={plan.proteinQuantity} onChange={(e) => handleMealPlanChange(index, "proteinQuantity", e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" placeholder="e.g. 200g" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-6">
                                        <div>
                                            <h4 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wider">Meal Preferences</h4>
                                            <div className="space-y-3">
                                                {MEAL_PREFERENCES.map((type) => (
                                                    <div key={type} className="flex flex-col sm:flex-row sm:items-center bg-white p-3 rounded-lg border border-gray-200">
                                                        <div className="w-32 font-bold text-gray-800 mb-2 sm:mb-0 text-sm">{type}</div>
                                                        <div className="flex flex-wrap gap-2 flex-1">
                                                            {DAYS.map((day) => (
                                                                <label key={day} className="flex items-center space-x-1.5 cursor-pointer bg-gray-50 px-2 py-1.5 rounded border border-gray-200 hover:bg-green-50 transition">
                                                                    <input
                                                                        type="radio"
                                                                        name={`pref-${index}-${day}`}
                                                                        checked={!!plan.days[day]?.includes(type)}
                                                                        onChange={() => handleDayPrefSelect(index, day, type, MEAL_PREFERENCES)}
                                                                        onClick={() => plan.days[day]?.includes(type) && handleDayPrefSelect(index, day, type, MEAL_PREFERENCES)}
                                                                        className="h-3.5 w-3.5 border-gray-300 text-green-600 focus:ring-green-500"
                                                                    />
                                                                    <span className="text-xs font-medium text-gray-700">{day.substring(0, 3)}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wider">Select Base</h4>
                                            <div className="space-y-3">
                                                {BASE_OPTIONS.map((type) => (
                                                    <div key={type} className="flex flex-col sm:flex-row sm:items-center bg-white p-3 rounded-lg border border-gray-200">
                                                        <div className="w-32 font-bold text-gray-800 mb-2 sm:mb-0 text-sm">{type}</div>
                                                        <div className="flex flex-wrap gap-2 flex-1">
                                                            {DAYS.map((day) => (
                                                                <label key={day} className="flex items-center space-x-1.5 cursor-pointer bg-gray-50 px-2 py-1.5 rounded border border-gray-200 hover:bg-green-50 transition">
                                                                    <input
                                                                        type="radio"
                                                                        name={`base-${index}-${day}`}
                                                                        checked={!!plan.days[day]?.includes(type)}
                                                                        onChange={() => handleDayPrefSelect(index, day, type, BASE_OPTIONS)}
                                                                        onClick={() => plan.days[day]?.includes(type) && handleDayPrefSelect(index, day, type, BASE_OPTIONS)}
                                                                        className="h-3.5 w-3.5 border-gray-300 text-green-600 focus:ring-green-500"
                                                                    />
                                                                    <span className="text-xs font-medium text-gray-700">{day.substring(0, 3)}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white py-4 shadow-[0_-10px_10px_-10px_rgba(0,0,0,0.05)]">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition shadow-sm font-medium"
                            >
                                Save Member
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="p-8 text-center text-gray-500">Loading members...</div>
            ) : !showForm && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Member Details
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subscription Info
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {members.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                                            No subscribed members found.
                                        </td>
                                    </tr>
                                ) : (
                                    members.map((member) => (
                                        <tr key={member._id} className="hover:bg-gray-50 transition-colors align-top">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-gray-900 text-base">{member.name}</div>
                                                <div className="text-sm text-gray-500">{member.email}</div>
                                                <div className="text-xs text-gray-400 font-mono mt-1">{member.mobile}</div>
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">
                                                        {member.numberOfMeals} Meals
                                                    </span>
                                                    {member.mealPlans && member.mealPlans.length > 0 && (
                                                        <div className="flex gap-1">
                                                            {Array.from(new Set(member.mealPlans.map(p => p.period))).map(period => (
                                                                <span key={period} className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-medium">{period}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {(member.customizationRequest || member.specialRequest) && (
                                                    <div className="space-y-2 text-xs max-w-sm">
                                                        {member.customizationRequest && (
                                                            <div className="bg-yellow-50 p-2 rounded border border-yellow-100">
                                                                <span className="font-semibold text-yellow-800 block mb-0.5">Customization request:</span>
                                                                <span className="text-yellow-700">{member.customizationRequest}</span>
                                                            </div>
                                                        )}
                                                        {member.specialRequest && (
                                                            <div className="bg-orange-50 p-2 rounded border border-orange-100">
                                                                <span className="font-semibold text-orange-800 block mb-0.5">Special request:</span>
                                                                <span className="text-orange-700">{member.specialRequest}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => setHistoryMember(member)}
                                                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
                                                        title="Meal History"
                                                    >
                                                        <History size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(member)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(member._id)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {historyMember && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
                    onClick={() => setHistoryMember(null)}
                >
                    <div className="bg-white rounded-2xl w-full max-w-3xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {historyMember.name} — Meal History
                            </h3>
                            <button onClick={() => setHistoryMember(null)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <MealHistory subscription={historyMember} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscribedMembersManagement;
